import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Plus, Loader2, Trash2, Clock, MapPin } from "lucide-react";

const STATUS = {
  agendada: "bg-chart-3/12 text-chart-3 border-chart-3/25",
  confirmada: "bg-success/12 text-success border-success/25",
  realizada: "bg-muted text-muted-foreground border-border",
  cancelada: "bg-destructive/12 text-destructive border-destructive/25",
};

export default function Agenda() {
  const [appts, setAppts] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ patient_id: "", datetime: "", type: "consulta", room: "" });

  const load = () => api.get("/appointments").then((r) => setAppts(r.data));
  useEffect(() => {
    load();
    api.get("/patients").then((r) => setPatients(r.data));
  }, []);

  const save = async () => {
    if (!f.patient_id || !f.datetime) { toast.error("Selecione paciente e data/hora"); return; }
    setSaving(true);
    try {
      await api.post("/appointments", f);
      toast.success("Agendamento criado");
      setOpen(false); setF({ patient_id: "", datetime: "", type: "consulta", room: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const setStatus = async (aid, status) => {
    await api.put(`/appointments/${aid}`, { status });
    setAppts((prev) => prev.map((a) => (a.id === aid ? { ...a, status } : a)));
  };

  const remove = async (aid) => { await api.delete(`/appointments/${aid}`); load(); };

  const fmt = (dt) => { try { return new Date(dt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }); } catch { return dt; } };

  return (
    <div className="p-6 sm:p-10 max-w-5xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-ai mb-2">Agenda</p>
          <h1 className="font-heading text-4xl font-light tracking-tighter">Consultas & exames</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-appointment-btn"><Plus className="w-4 h-4 mr-2" /> Agendar</Button>
          </DialogTrigger>
          <DialogContent data-testid="appointment-dialog">
            <DialogHeader>
              <DialogTitle>Novo agendamento</DialogTitle>
              <DialogDescription>Marque consulta ou exame para um paciente.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Paciente</Label>
                <Select value={f.patient_id} onValueChange={(v) => setF({ ...f, patient_id: v })}>
                  <SelectTrigger data-testid="appt-patient-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Data e hora</Label>
                  <Input type="datetime-local" value={f.datetime} onChange={(e) => setF({ ...f, datetime: e.target.value })} data-testid="appt-datetime-input" /></div>
                <div className="space-y-2"><Label>Tipo</Label>
                  <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                    <SelectTrigger data-testid="appt-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="consulta">Consulta</SelectItem>
                      <SelectItem value="exame">Exame</SelectItem>
                      <SelectItem value="retorno">Retorno</SelectItem>
                      <SelectItem value="teleconsulta">Teleconsulta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2"><Label>Sala</Label>
                <Input value={f.room} onChange={(e) => setF({ ...f, room: e.target.value })} data-testid="appt-room-input" /></div>
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving} data-testid="save-appointment-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Agendar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {appts.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground border-dashed">
          <CalendarDays className="w-8 h-8 mx-auto mb-3 opacity-40" /> Nenhum agendamento.
        </Card>
      ) : (
        <div className="space-y-3">
          {appts.map((a) => (
            <Card key={a.id} className="p-5 border-border shadow-none" data-testid={`appointment-${a.id}`}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex flex-col items-center justify-center">
                    <CalendarDays className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium">{a.patient_name}</p>
                    <p className="text-sm text-muted-foreground flex items-center gap-3">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmt(a.datetime)}</span>
                      <span className="capitalize">{a.type}</span>
                      {a.room && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {a.room}</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}>
                    <SelectTrigger className="w-36" data-testid={`appt-status-${a.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["agendada", "confirmada", "realizada", "cancelada"].map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Badge variant="outline" className={STATUS[a.status] || STATUS.agendada}>{a.status}</Badge>
                  <Button size="icon" variant="ghost" onClick={() => remove(a.id)} data-testid={`delete-appointment-${a.id}`}>
                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
